import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { firebaseAuth } from "../../../components/firebase/auth";
import { firebaseStore } from "../../../components/firebase/firestore";
import { mainImageForLanguage } from "../processedProductMedia";
import type { ProcessedProductMedia } from "../processedProductMedia";
import { shopAdminApi, type ShopCatalogRebuildPreview,
  type ShopCatalogRebuildStatus } from "./shopAdminApi";
import { validWebshopPrice, validWebshopVat } from "./offerInput";
import type { User } from "firebase/auth";
import ShopOfferEditor from "./ShopOfferEditor";
import OfferHistory from "./OfferHistory";
import { shopOfferId, type ShopOfferIntent } from "./shopOfferModel";
import WebshopContentReview from "./WebshopContentReview";
import WebshopCatalogDeletion from "./WebshopCatalogDeletion";
import type { ShopCatalogKind } from "./shopAdminApi";
import "./webshopCatalog.css";

type Row = { docId: string; data: Record<string, unknown> };
type Source = "products" | "packages" | "stock";
const sources: Record<Source, string> = {
  products: "products_freezedry", packages: "products_freezedry_package", stock: "products_freezedry_batch",
};
const string = (value: unknown) => typeof value === "string" ? value.trim() : "";
const positive = (value: unknown) => typeof value === "number" && Number.isFinite(value) && value > 0;
const count = (value: unknown) => Number.isSafeInteger(value) && (value as number) >= 0 ? value as number : null;
const businessId = (row: Row) => string(row.data.id) || row.docId;
const validDate = (value: unknown): value is string => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
const stockholmDate = () => new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Stockholm", year: "numeric",
  month: "2-digit", day: "2-digit" }).format(new Date());
function eligibleUnits(data: Record<string, unknown>, packageId: string, today: string): number {
  if (string(data.packageId) !== packageId || data.shopSaleStatus !== "approved") return 0;
  const quantity = count(data.quantity);
  const reserved = count(data.reservedQuantity);
  const refs = Array.isArray(data.sourceBatchRefs) ? data.sourceBatchRefs : [data.sourceBatchRef];
  if (quantity === null || reserved === null || reserved > quantity || refs.length < 1 || refs.length > 20 ||
      refs.some((ref) => !string(ref)) || new Set(refs).size !== refs.length ||
      !string(data.processedBy) || !string(data.packedBy) ||
      !validDate(data.processDate) || !validDate(data.packingDate) || !validDate(data.bestBeforeDate) ||
      data.processDate > data.packingDate || data.packingDate > data.bestBeforeDate || data.bestBeforeDate < today) return 0;
  return quantity - reserved;
}

function packageReasons(pkg: Row, packages: Row[], stock: Row[], duplicate: boolean, today: string): string[] {
  const data = pkg.data;
  const reasons: string[] = [];
  if (duplicate) reasons.push("Dubblett av förpacknings-ID");
  if (data.active === false || data.shopEnabled === false) reasons.push("Inaktiv för webbshop");
  if (data.productForm === "capsules") reasons.push("Kapslar kräver separat klassificering");
  if (!positive(data.priceWebshop)) reasons.push("Webbshoppris saknas");
  else if (!validWebshopPrice(data.priceWebshop)) reasons.push("Webbshoppriset kan inte användas i kassan");
  if (!validWebshopVat(data.vatRate)) reasons.push("Momssats saknas eller är ogiltig (högst två decimaler)");
  if (!positive(data.size)) reasons.push("Nettovikt saknas");
  if (!positive(data.packageWeight)) reasons.push("Tom förpackningsvikt saknas");
  const dims = data.packageDimensions as Record<string, unknown> | null | undefined;
  if (!dims || !positive(dims.lengthCm) || !positive(dims.widthCm) || !positive(dims.heightCm)) reasons.push("Mått saknas");
  if (!string(data.shippingProfileId)) reasons.push("Granskad packprofil saknas (kopplas under Frakt)");
  if (!string(data.sku)) reasons.push("SKU saknas");
  let basePackageId = businessId(pkg);
  let multiplier = 1;
  if (data.packageType === "box") {
    const base = packages.filter((item) => businessId(item) === string(data.boundPackageId) && string(item.data.parentId) === string(data.parentId) && item.data.packageType !== "box");
    if (base.length !== 1 || !positive(data.unitsPerBox) || !Number.isSafeInteger(data.unitsPerBox)) reasons.push("Boxkoppling saknas eller är tvetydig");
    else { basePackageId = businessId(base[0]); multiplier = data.unitsPerBox as number; }
  } else if (data.packageType !== "bag" && data.packageType !== "can") reasons.push("Okänd förpackningstyp");
  const linked = stock.filter((row) => string(row.data.packageId) === basePackageId && string(row.data.productId) === string(data.parentId));
  const available = linked.reduce((sum, row) => sum + eligibleUnits(row.data, basePackageId, today), 0);
  if (Math.floor(available / multiplier) === 0) reasons.push("Inget granskat säljbart lager");
  return reasons;
}

export default function WebshopCatalog({ canManage = false, previewOnly = false }: { canManage?: boolean; previewOnly?: boolean }) {
  const [user] = useAuthState(firebaseAuth);
  const intent = useRef<ShopOfferIntent | null>(null);
  const [intentVersion, setIntentVersion] = useState(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  return <CatalogPage key={`${user?.uid || "signed-out"}:${canManage}:${previewOnly}`} user={user || null}
    canManage={canManage && !previewOnly} intent={intent} intentVersion={intentVersion}
    onIntentSettled={() => { if (mounted.current) setIntentVersion((value) => value + 1); }} />;
}
function CatalogPage({ user, canManage, intent, intentVersion, onIntentSettled }: { user: User | null; canManage: boolean;
  intent: React.MutableRefObject<ShopOfferIntent | null>; intentVersion: number; onIntentSettled: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Record<Source, Row[]>>({ products: [], packages: [], stock: [] });
  const [loaded, setLoaded] = useState<Record<Source, boolean>>({ products: false, packages: false, stock: false });
  const [editTarget, setEditTarget] = useState<string | null>(null);
  const [offerSaved, setOfferSaved] = useState(false);
  const [offerHistoryRefresh, setOfferHistoryRefresh] = useState(0);
  const [denied, setDenied] = useState(false);
  const [reload, setReload] = useState(0);
  const live = useRef(true);
  const accessDenied = useRef(false);
  const hideDenied = () => {
    accessDenied.current = true;
    setDenied(true); setRows({ products: [], packages: [], stock: [] }); setEditTarget(null);
    setRepairTarget(null); setRepairPreview(null); setRepairStatus(null); setRepairJobId("");
    setContentTarget(null); setDeleteTarget(null); setContentSaved(false); setOfferSaved(false);
  };
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const [repairTarget, setRepairTarget] = useState<string | null>(null);
  const [repairReason, setRepairReason] = useState("");
  const [repairPreview, setRepairPreview] = useState<ShopCatalogRebuildPreview | null>(null);
  const [repairJobId, setRepairJobId] = useState("");
  const [repairStatus, setRepairStatus] = useState<ShopCatalogRebuildStatus | null>(null);
  const [repairError, setRepairError] = useState("");
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairOperationKey, setRepairOperationKey] = useState("");
  const [today, setToday] = useState(stockholmDate);
  const [contentTarget, setContentTarget] = useState<string | null>(null);
  const [contentSaved, setContentSaved] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ kind: ShopCatalogKind; documentId: string; name: string } | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setToday(stockholmDate()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!repairJobId || !user || denied) return;
    let active = true;
    const poll = async () => {
      try {
        const status = await shopAdminApi.catalogRebuildStatus(user, repairJobId);
        if (!active || accessDenied.current) return;
        setRepairStatus(status);
        if (status.state === "succeeded" || status.state === "failed") window.clearInterval(timer);
      } catch { if (active && !accessDenied.current) setRepairError("Reparationens status kunde inte hämtas. Försök igen senare."); }
    };
    const timer = window.setInterval(poll, 2000);
    void poll();
    return () => { active = false; window.clearInterval(timer); };
  }, [repairJobId, user, denied]);

  const openRepair = (docId: string) => {
    if (intent.current || denied || !canManage) return;
    setRepairTarget(docId); setRepairReason(""); setRepairPreview(null);
    setRepairJobId(""); setRepairStatus(null); setRepairError("");
    setRepairOperationKey(crypto.randomUUID());
  };
  const previewRepair = async () => {
    if (!user || !canManage || denied || intent.current || !repairTarget || !repairReason.trim() || repairBusy) return;
    setRepairBusy(true); setRepairError("");
    try { const preview = await shopAdminApi.previewCatalogRebuild(user, repairTarget, repairReason.trim()); if (live.current && !accessDenied.current) setRepairPreview(preview); }
    catch { if (live.current && !accessDenied.current) setRepairError("Förhandsgranskningen kunde inte hämtas. Kontrollera anslutningen och försök igen."); }
    finally { if (live.current && !accessDenied.current) setRepairBusy(false); }
  };
  const executeRepair = async () => {
    if (!user || !canManage || denied || intent.current || !repairTarget || !repairPreview || repairBusy) return;
    setRepairBusy(true); setRepairError("");
    try {
      const job = await shopAdminApi.executeCatalogRebuild(user, repairTarget, repairReason.trim(), repairOperationKey);
      if (live.current && !accessDenied.current) setRepairJobId(job.jobId);
    } catch { if (live.current && !accessDenied.current) setRepairError("Reparationen kunde inte köas. Granska aktuella uppgifter och försök igen."); }
    finally { if (live.current && !accessDenied.current) setRepairBusy(false); }
  };

  useEffect(() => {
    const offer = new URLSearchParams(location.search).get("offer");
    if (!offer || !loaded.packages) return;
    if (canManage && !denied && shopOfferId(offer) && (!intent.current || (intent.current.uid === user?.uid && intent.current.id === offer)) && rows.packages.some((item) => item.docId === offer)) setEditTarget(offer);
    const search = new URLSearchParams(location.search); search.delete("offer");
    navigate(`${location.pathname}${search.toString() ? `?${search}` : ""}`, { replace: true });
  }, [location.search, location.pathname, loaded.packages, canManage, denied, rows.packages, navigate, intent, user?.uid]);

  useEffect(() => {
    if (!user || denied) return;
    let active = true;
    const stops = (Object.keys(sources) as Source[]).map((source) => onSnapshot(collection(firebaseStore, sources[source]),
      (snapshot) => {
        if (!active || accessDenied.current) return;
        setRows((current) => ({ ...current, [source]: snapshot.docs.map((entry) => ({ docId: entry.id, data: entry.data() })) }));
        setLoaded((current) => ({ ...current, [source]: true }));
      }, () => { if (active && !accessDenied.current) hideDenied(); }));
    return () => { active = false; stops.forEach((stop) => stop()); };
    // This keyed page owns one auth/capability context; denied reads require explicit reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, denied, reload]);

  const products = useMemo(() => [...rows.products].sort((a, b) => string(a.data.name_sv).localeCompare(string(b.data.name_sv), "sv")), [rows.products]);
  const duplicateProducts = useMemo(() => {
    const counts = new Map<string, number>();
    products.forEach((row) => counts.set(businessId(row), (counts.get(businessId(row)) || 0) + 1));
    return counts;
  }, [products]);
  const packageCounts = useMemo(() => {
    const counts = new Map<string, number>();
    rows.packages.forEach((row) => counts.set(businessId(row), (counts.get(businessId(row)) || 0) + 1));
    return counts;
  }, [rows.packages]);

  if (!user) return <Alert severity="warning">Logga in för att läsa sortimentet.</Alert>;
  if (denied) return <Alert severity="error">Sortimentsbehörigheten kunde inte verifieras. Inga sparade uppgifter visas.
    <Button onClick={() => { accessDenied.current = false; setDenied(false); setLoaded({ products: false, packages: false, stock: false }); setReload((value) => value + 1); }}>Hämta aktuell behörighet</Button></Alert>;
  const blocked = !!intent.current;
  return <div className="DashBoardTabInner FreshDashboardTab WebshopCatalog">
    <div className="FreshTabToolbar"><div><p className="FreshEyebrow">Webshop</p><h2 className="FreshTabTitle">Sortiment</h2>
      <p className="FreshTabSummary">Alla processade produkter visas här. Publicering styrs enbart i Processade produkter; lager och försäljningsberedskap granskas separat.</p></div></div>
    <Alert severity="info">Beredskapskontrollen är vägledande. Köp är avstängda tills lagertransaktioner, frakt, moms och betalning är färdigtestade.</Alert>
    {offerSaved && <Alert severity="success" onClose={() => setOfferSaved(false)}>Prisändringen är registrerad. Listan uppdateras från förpackningen; köp är fortsatt avstängda.</Alert>}
    {blocked && <Alert severity="warning">En prisändring är obekräftad. Återgå till samma förpackning och försök igen med samma uppgifter. Granska aktuella uppgifter efter en fullständig omladdning.</Alert>}
    {contentSaved && <Alert severity="success" onClose={() => setContentSaved(false)}>Innehållsgranskningen har registrerats. Produktens publicering är oförändrad och köp är fortsatt avstängda.</Alert>}
    <OfferHistory user={user} canRead={canManage} refreshVersion={offerHistoryRefresh} onDenied={hideDenied} />
    {!Object.values(loaded).every(Boolean) ? <div role="status"><CircularProgress size={24} /> Hämtar sortiment…</div> :
      <div className="WebshopCatalogList">{products.length === 0 && <p>Inga processade produkter finns ännu.</p>}
        {products.map((product) => {
          const id = businessId(product);
          const packages = rows.packages.filter((pkg) => string(pkg.data.parentId) === id);
          const reasons = [
            ...(duplicateProducts.get(id)! > 1 ? ["Dubblett av produkt-ID"] : []),
            ...(!string(product.data.name_sv) ? ["Svenskt namn saknas"] : []),
            ...(!string(product.data.ingredients_sv) ? ["Svenska ingredienser saknas"] : []),
            ...(!string(product.data.storeData_sv) ? ["Svensk förvaringstext saknas"] : []),
            ...(!string(product.data.shelfLife_sv) ? ["Svensk hållbarhet saknas"] : []),
            ...(!mainImageForLanguage(product.data as ProcessedProductMedia, "sv") ? ["Svensk huvudbild saknas"] : []),
            ...(!packages.length ? ["Ingen förpackning"] : []),
          ];
          return <article className="WebshopCatalogProduct" key={product.docId}>
            <div className="WebshopCatalogProductHeader">
              {mainImageForLanguage(product.data as ProcessedProductMedia, "sv") && <img src={mainImageForLanguage(product.data as ProcessedProductMedia, "sv")} alt="" loading="lazy" />}
              <div><h3>{string(product.data.name_sv) || "Produkt utan namn"}</h3><p>{string(product.data.name_en) || "Engelskt namn saknas"}</p>
                <strong>{product.data.isPublic === true ? "Publik i webbshoppen" : "Dold i webbshoppen"}</strong></div>
              <Button component={Link} to={`/processed/products?edit=${encodeURIComponent(product.docId)}`} size="small">Redigera produkt</Button>
              {canManage && <Button disabled={blocked} size="small" onClick={() => openRepair(product.docId)}>Reparera publik vy</Button>}
              {canManage && <Button disabled={blocked} size="small" onClick={() => { setContentSaved(false); setContentTarget(product.docId); }}>Granska produktinformation</Button>}
              {canManage && <Button disabled={blocked} size="small" color="error" onClick={() => setDeleteTarget({ kind: "product", documentId: product.docId,
                name: string(product.data.name_sv) || "Produkt utan namn" })}>Kontrollera borttagning</Button>}
            </div>
            <p>Innehållsgodkännande måste matcha aktuell produktinformation. Öppna granskningen för serverns kontroll.</p>
            {reasons.length > 0 && <p className="WebshopCatalogReasons">Produkt: {reasons.join(" · ")}</p>}
            {packages.map((pkg) => {
              const issues = packageReasons(pkg, packages, rows.stock, (packageCounts.get(businessId(pkg)) || 0) > 1, today);
              return <div className="WebshopCatalogPackage" key={pkg.docId}>
                <div><strong>{string(pkg.data.formatedSize) || `${pkg.data.size || "?"} g`} · {string(pkg.data.packageType)}</strong>
                  <span>{typeof pkg.data.priceWebshop === "number" ? `${pkg.data.priceWebshop} kr inkl. moms` : "Pris saknas"}</span></div>
                <p>{issues.length ? issues.join(" · ") : "Källdata kontrollerad; checkout ännu avstängd"}</p>
                <Button component={Link} to={`/processed/packages?edit=${encodeURIComponent(pkg.docId)}`} size="small">Redigera förpackning</Button>
                {canManage && <Button disabled={!shopOfferId(pkg.docId) || (blocked && (intent.current?.uid !== user.uid || intent.current?.id !== pkg.docId))} size="small" onClick={() => { setOfferSaved(false); setEditTarget(pkg.docId); }}>Webbshoppris och moms</Button>}
                {canManage && <Button disabled={!shopOfferId(pkg.docId)} size="small" onClick={() => {
                  const search = new URLSearchParams(location.search); search.set("offerHistoryId", pkg.docId); search.delete("offerHistoryCursor");
                  navigate(`${location.pathname}?${search}`);
                }}>Prishistorik</Button>}
                {canManage && <Button disabled={blocked} size="small" color="error" onClick={() => setDeleteTarget({ kind: "package", documentId: pkg.docId,
                  name: string(product.data.name_sv) + " · " + (string(pkg.data.formatedSize) || businessId(pkg)) })}>Kontrollera borttagning</Button>}
              </div>;
            })}
          </article>;
        })}
      </div>}
    {canManage && editTarget && (!intent.current || (intent.current.uid === user.uid && intent.current.id === editTarget)) &&
      <ShopOfferEditor key={editTarget} user={user} packageDocId={editTarget} intent={intent} intentVersion={intentVersion}
        onIntentSettled={onIntentSettled} onClose={() => setEditTarget(null)} onDenied={hideDenied} onSaved={() => {
          setOfferSaved(true);
          const search = new URLSearchParams(location.search);
          if (search.get("offerHistoryId") === editTarget) {
            search.delete("offerHistoryCursor"); navigate(`${location.pathname}?${search}`, { replace: true });
            setOfferHistoryRefresh((value) => value + 1);
          }
        }} />}
    <Dialog open={!!repairTarget} onClose={() => { if (!repairBusy) setRepairTarget(null); }} fullWidth maxWidth="sm">
      <DialogTitle>Reparera publik produktvy</DialogTitle>
      <DialogContent className="WebshopOfferDialog">
        <p>En reparation bygger om härledda erbjudanden från Processade produkter, förpackningar och Processat lager. Produktens publiceringsval ändras inte.</p>
        <TextField label="Orsak" value={repairReason} onChange={(event) => {
          setRepairReason(event.target.value); setRepairPreview(null); setRepairOperationKey(crypto.randomUUID());
        }} fullWidth size="small" required inputProps={{ maxLength: 500 }} />
        {repairError && <Alert severity="error">{repairError}</Alert>}
        {repairPreview && <Alert severity={repairPreview.ambiguousProduct ? "warning" : "info"}>
          Nuvarande erbjudanden: {repairPreview.currentOffers}. Efter ombyggnad: {repairPreview.projectedOffers}.
          Tas bort: {repairPreview.offersToDelete}. Skrivs om: {repairPreview.offersToWrite}.
          {repairPreview.ambiguousProduct && " Produkt-ID är tvetydigt; rätta källdatan innan försäljning."}
        </Alert>}
        {repairJobId && <Alert severity={repairStatus?.state === "failed" ? "error" : repairStatus?.state === "succeeded" ? "success" : "info"}>
          {repairStatus?.state === "succeeded" ? "Den publika vyn har byggts om." :
            repairStatus?.state === "failed" ? "Ombyggnaden misslyckades. Kontrollera källdatan och kontakta utvecklaren." :
              "Ombyggnaden är köad. Väntar på resultat…"}
        </Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRepairTarget(null)} disabled={repairBusy}>Stäng</Button>
        <Button onClick={previewRepair} disabled={repairBusy || !repairReason.trim() || !!repairJobId}>Förhandsgranska</Button>
        <Button onClick={executeRepair} disabled={repairBusy || !repairPreview || !!repairJobId || !repairReason.trim()}>Köa reparation</Button>
      </DialogActions>
    </Dialog>
    <WebshopContentReview productDocId={contentTarget} user={user} onClose={() => setContentTarget(null)} onSaved={() => setContentSaved(true)} />
    {deleteTarget && <WebshopCatalogDeletion key={`${deleteTarget.kind}_${deleteTarget.documentId}`} {...deleteTarget}
      user={user} onClose={() => setDeleteTarget(null)} />}
  </div>;
}
